import { gql } from '@apollo/client';

// Grants module, Funder portal (IB-1897). The server enforces every access rule; these documents only fetch.

export const GRANTS_ACCESS = gql`
  query GrantsAccess {
    grantsAccess {
      allowed
      level
      reason
      message
      isOrganisationAdmin
      plan
      settings {
        reviewRequired
        financialYearStartMonth
        defaultCurrency
      }
    }
  }
`;

export const GRANT_SETTINGS = gql`
  query GrantSettings {
    grantSettings: grantSettingsView {
      settings {
        reviewRequired
        financialYearStartMonth
        defaultCurrency
      }
      users {
        userId
        name
        email
        role
        level
        isOrganisationAdmin
      }
    }
  }
`;

export const GRANT_FORM_OPTIONS = gql`
  query GrantFormOptions {
    grantFormOptions {
      grantees {
        id
        name
        registrationNumber
        npoNumber
      }
      programmes {
        id
        name
        interventions {
          id
          name
        }
      }
      defaultCurrency
    }
  }
`;

export const GRANT_LIST_FIELDS = `
  id
  createdAt
  reference
  name
  grantee { id name registrationNumber npoNumber }
  programme { id name }
  intervention { id name }
  currency
  approvedAmount
  approvalDate
  startDate
  endDate
  lifecycle
  status
  statusDate
  reviewComment
  notes
  source
  externalId
  agreementFile { id name url }
  capturedById
  capturedBy
  reviewedBy
  importBatchId
  importFileName
  plannedTotal
  cumulativeActual
  percentDisbursed
`;

export const GRANT_TRANSACTION_FIELDS = `
  id
  createdAt
  grantId
  grantReference
  grantStatus
  type
  amount
  transactionDate
  trancheNumber
  plannedTrancheId
  plannedTrancheNumber
  reference
  condition
  notes
  status
  statusDate
  reviewComment
  source
  externalId
  capturedById
  capturedBy
  reviewedBy
  importBatchId
  importFileName
`;

export const GRANTS = gql`
  query Grants($input: GrantsFilterInput) {
    grants: grantList(input: $input) {
      total
      items {
        ${GRANT_LIST_FIELDS}
      }
    }
  }
`;

export const GRANT_DETAIL_FIELDS = `
  grant { ${GRANT_LIST_FIELDS} }
  transactions { ${GRANT_TRANSACTION_FIELDS} }
  history { timestamp action user label }
  nextPlannedTrancheDate
  nextPlannedTrancheAmount
  canEdit
  canReview
`;

export const GRANT = gql`
  query Grant($id: ID!) {
    grant: grantDetail(id: $id) {
      ${GRANT_DETAIL_FIELDS}
    }
  }
`;

export const PERIOD_FIELDS = `
  key
  label
  start
  end
  planned
  actual
  cumulativePlanned
  cumulativeActual
  newApprovals
`;

export const GRANT_SCHEDULE = gql`
  query GrantSchedule($input: GrantScheduleInput!) {
    grantSchedule(input: $input) {
      approvedAmount
      currency
      periods { ${PERIOD_FIELDS} }
    }
  }
`;

export const GRANTS_EXPORT = gql`
  query GrantsExport($input: GrantsFilterInput) {
    grantsExport(input: $input) {
      fileName
      content
    }
  }
`;

export const GRANT_IMPORT_TEMPLATE = gql`
  query GrantImportTemplate($type: String!) {
    grantImportTemplate(type: $type) {
      fileName
      content
    }
  }
`;

export const GRANT_REVIEW_QUEUE = gql`
  query GrantReviewQueue {
    grantReviewQueue {
      batchId
      fileName
      type
      createdAt
      uploadedBy
      grants { ${GRANT_LIST_FIELDS} }
      transactions { ${GRANT_TRANSACTION_FIELDS} }
    }
  }
`;

const FIGURES = `
  approved
  newApprovals
  planned
  actual
  cumulativePlanned
  cumulativeActual
  variance
  percentDisbursed
  undisbursed
  nextTrancheDate
  nextTrancheAmount
`;

export const GRANT_SUMMARY = gql`
  query GrantSummary($input: GrantSummaryInput) {
    grantSummary(input: $input) {
      currency
      currencies
      groupedBy
      level
      from
      to
      periodSize
      provisional
      financialYearStartMonth
      series { ${PERIOD_FIELDS} }
      totals { ${FIGURES} }
      rows {
        key
        label
        figures { ${FIGURES} }
        grants { id reference name programmeName figures { ${FIGURES} } }
      }
      grantees { id name }
      programmes { id name }
    }
  }
`;

export const GRANT_SUMMARY_EXPORT = gql`
  query GrantSummaryExport($input: GrantSummaryInput) {
    grantSummaryExport(input: $input) {
      fileName
      content
    }
  }
`;
